import {css} from 'aphrodite';
import React, {useMemo} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {getDataFrameSpecFromGetAnalysisDataFrame} from 'analysis-tool/AnalysisTypes';
import {StepDataFrameLineageNode} from 'analysis-tool/data/DatasetLineageUtils';
import AnalysisTableSelector from 'analysis-tool/ui/components/AnalysisTableSelector';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate, {
  StepConfigurationViewTemplateProps,
} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigEditorPane from 'pipelines/generalized-step-config-editor/StepConfigEditorPane';
import {PreviewOutput} from 'pipelines/PipelineDAO';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {validatePipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
} from 'ui-library/hooks/useAsyncState';
import {stepperStyles, StepperV2} from 'ui-library/stepper/Stepper';
import {JSONValue, MakeFieldsRequired} from 'util/Types';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from '../DataFrameCreationConstants';
import {buildPreviewFailureMessage} from '../DataFrameCreationMessages';
import {getSharedDataFrameCreationMethodProps, openSelectedItemForNewStep} from '../DataFrameCreationUtils';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';

const PREVIEW_LIMIT_MESSAGE = 'This is a preview, limited to the first 5 plates or entities.';

const stepInputGlobalState = makeUseGlobalClientState<MakeFieldsRequired<
  StepDataFrameLineageNode,
  'data'
> | null | null>(null);
const stepConfigGlobalState = makeUseGlobalClientState<Record<string, JSONValue> | null>(null);
const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);
const previewAsyncGlobalState =
  makeUseGlobalClientState<AsyncState<PreviewOutput<PipelineStepType.LOOK_UP>>>(NOT_FETCHED_ASYNC_STATE);

export type CreateDataFrameFromLookUpViewProps = Pick<
  MainViewComponentProps<AnalysisMainViewItemType.LOOK_UP>,
  | 'analysis'
  | 'clientStateId'
  | 'selectedItem'
  | 'onCloseStepInitializationView'
  | 'onOpenStepInitializationView'
>;

export default function CreateDataFrameFromLookUpView({
  analysis,
  clientStateId,
  selectedItem,
  onCloseStepInitializationView,
  onOpenStepInitializationView,
}: CreateDataFrameFromLookUpViewProps): JSX.Element {
  const [stepInput, setStepInput] = stepInputGlobalState.useGlobalClientState(clientStateId);
  const [stepConfig, setStepConfig] = stepConfigGlobalState.useGlobalClientState(clientStateId);
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);

  function clearState(): void {
    setStepInput(null);
    setStepConfig({});
    setIsFinalizing(false);
  }

  const {isComplete, errors} =
    stepInput && stepConfig
      ? validatePipelineStepConfig(PipelineStepType.LOOK_UP, 0, stepConfig, {
          'Input dataset': [getDataFrameSpecFromGetAnalysisDataFrame(stepInput.data.dataFrame)],
        })
      : {isComplete: false, errors: []};

  const [previewState, setPreviewState] = previewAsyncGlobalState.useGlobalClientState(clientStateId);

  useAutoUpdatePreview(
    isComplete && !errors.length && stepInput
      ? {
          stepType: PipelineStepType.LOOK_UP,
          stepInput: {'Input dataset': stepInput.data.dataFrame.id},
          stepConfig: {...stepConfig, dataFrameName: ''} as PipelineStepConfig<PipelineStepType.LOOK_UP, 0>,
        }
      : null,
    (data, setAsyncState) => {
      AnalysisActions.runPreviewAnalysisStep<PipelineStepType.LOOK_UP>(
        data,
        {status: AsyncStatus.NOT_FETCHED},
        setAsyncState
      );
    },
    setPreviewState
  );

  const previewDataFrame: DataFrame | null =
    getMostRecentData(previewState)?.dataFrames['Output dataset'][0] ?? null;

  const onCancel: StepConfigurationViewTemplateProps['onCancel'] = (...args) => {
    onCloseStepInitializationView(...args);
    clearState();
  };

  async function onFinalize(): Promise<void> {
    assert(
      stepInput != null && (isComplete ?? false) && errors.length === 0,
      'Tried to finalize with incomplete form'
    );
    setIsFinalizing(true);

    try {
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(analysis.id, {
        stepType: PipelineStepType.LOOK_UP,
        stepVersion: 0,
        stepConfig: stepConfig as PipelineStepConfig<PipelineStepType.LOOK_UP, 0>,
        stepInputParents: {
          'Input dataset': [{stepId: stepInput.step.id, stepDataName: stepInput.data.outputStepData.name}],
        },
        stepInput: {inputDataFrameId: stepInput.data.dataFrame.id},
      });
      openSelectedItemForNewStep(newStep, onCloseStepInitializationView);
    } finally {
      setIsFinalizing(false);
      clearState();
    }
  }

  const dataFramesInfos = useMemo(
    () => ({
      'Input dataset':
        stepInput == null
          ? []
          : [{dataFrameSpec: getDataFrameSpecFromGetAnalysisDataFrame(stepInput.data.dataFrame)}],
    }),
    [stepInput]
  );

  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(selectedItem)}
      clientStateId={clientStateId}
      dataFrames={
        previewDataFrame ? [{...previewDataFrame, name: String(stepConfig?.dataFrameName ?? '')}] : []
      }
      disableFinalizeButton={!isComplete || errors.length > 0 || stepInput == null}
      isFinalizing={isFinalizing}
      isLoadingPreview={previewState.status === AsyncStatus.LOADING}
      messages={
        previewState.status === AsyncStatus.ERROR
          ? [buildPreviewFailureMessage()]
          : previewDataFrame
            ? [{text: PREVIEW_LIMIT_MESSAGE, bannerType: InlineBannerType.INFO}]
            : errors.map((error) => ({text: error, bannerType: InlineBannerType.WARNING}))
      }
      noPreviewPlaceholderOptions={{
        title: DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT,
        subtitle: 'Select plates or entities to look up from, and select columns for the new table.',
      }}
      onCancel={onCancel}
      onFinalize={onFinalize}
      title="Look up"
      toolbar={
        <ChooseDifferentDataFrameCreationMethodButton
          onOpenStepInitializationView={(newSelectedItem: {
            type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
          }) => {
            clearState();
            onOpenStepInitializationView({...newSelectedItem});
          }}
        />
      }
    >
      <StepperV2
        steps={[
          {
            title: 'Select table to look up from',
            content: (
              <AnalysisTableSelector
                analysis={analysis}
                includeViews={true}
                selectedLineageNode={stepInput}
                onSelect={setStepInput}
              />
            ),
            key: 'select-tables',
          },
          stepInput && {
            title: 'Configure look up',
            content: (
              <StepConfigEditorPane
                stepType={PipelineStepType.LOOK_UP}
                stepVersion={0}
                config={stepConfig}
                setConfig={setStepConfig}
                dataFramesInfos={dataFramesInfos}
                clientStateId={clientStateId}
              />
            ),
            key: 'look-up',
          },
        ].map(
          (step) =>
            step && {
              ...step,
              title: <div className={css(stepperStyles.defaultStepTitle)}>{step.title}</div>,
              content: <div className={css(stepperStyles.defaultStepContent)}>{step.content}</div>,
            }
        )}
      />
    </StepConfigurationViewTemplate>
  );
}
